import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import pluralize from "pluralize";
import {
  Box,
  Container,
  Grid,
  Link,
  Typography,
  Stack,
  IconButton,
} from "@mui/material";
import FacebookRoundedIcon from "@mui/icons-material/FacebookRounded";
import TwitterIcon from "@mui/icons-material/Twitter";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";

import AdBanner from "../components/AdBanner";
import Quize from "../components/Quize";
import {
  getChallengeById,
  getOtherChallenges,
  getChallengesByName,
} from "../helper/program.helper";
import { logEngagement } from "../helper/log.helper";
import OtherChallengeItem from "../components/OtherChallengeItem";
import ISISection from "../components/ISISection";
import Pairs from "../components/Pairs";
import GameResult from "../components/GameResult";
import PoirotGame from "../components/PoirotGame";
import LoadingSpinner from "../components/LoadingSpinner";
import privacyOptions from "../assets/privacyoptions.png";

function decodeHtml(html) {
  var txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

const Challenge = () => {
  const { challengeName } = useParams();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const challengeId = searchParams.get("challengeid");
  const npi = searchParams.get("npi");
  const hem = searchParams.get("hem");
  const utmSource = searchParams.get("utm_source");
  const utmMedium = searchParams.get("utm_medium");
  const utmCampaign =
    searchParams.get("utm_campaign") || searchParams.get("utm_campaign ");

  const queryParams = new URLSearchParams();
  if (npi) queryParams.append("npi", npi);
  if (utmSource) queryParams.append("utm_source", utmSource);
  if (utmMedium) queryParams.append("utm_medium", utmMedium);
  if (utmCampaign) queryParams.append("utm_campaign", utmCampaign);
  if (hem) queryParams.append("hem", hem);

  const settings = useSelector((state) => state.siteSettings);
  const copyrightText =
    settings?.copyright_text ||
    `© ${new Date().getFullYear()} Point Of Care Network, LLC. All Rights Reserved.`;
  const ipAddress = useSelector((state) => state.ipAddress);

  const [challenge, setChallenge] = useState();
  const [otherChallenges, setOtherChallenges] = useState([]);
  const [pairImages, setPairImages] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [displayTime, setDisplayTime] = useState(10);
  const [open, setOpen] = useState(false);
  const [gameResult, setGameResult] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [response, setResponse] = useState({
    status: false,
    message: "",
  });
  const [loading, setLoading] = useState(true);
  const [isAnswered, setIsAnswered] = useState(false);
  const [additionalChallengeType, setAdditionalChallengeType] = useState("");

  useEffect(() => {
    if (ipAddress !== "" && challenge) {
      let payload = {
        npi: npi,
        hem: hem,
        ipAddress: ipAddress,
        engagementRefId: "Challenge",
        engagementSecRefId: challenge.id,
        engagementType: "Page View",
        utmSource: utmSource,
        utmMedium: utmMedium,
        utmCampaign,
        timeToComplete: "",
        gameResult: "",
      };
      logEngagement(payload);

      payload = {
        npi: npi,
        hem: hem,
        ipAddress: ipAddress,
        engagementRefId: "Challenge",
        engagementSecRefId: challenge.id,
        engagementType: "Challenge Started",
        utmSource: utmSource,
        utmMedium: utmMedium,
        utmCampaign,
        timeToComplete: "",
        gameResult: "",
      };
      logEngagement(payload);
    }
  }, [ipAddress, challenge]);

  useEffect(() => {
    if (challengeId) {
      getChallengeById(challengeId)
        .then(({ challenge }) => {
          setChallenge(challenge);

          if (challenge && challenge.type === "Pairs") {
            if (JSON.parse(challenge.gameSection).gameType === "standard") {
              setPairImages(JSON.parse(challenge.images));
            } else {
              const qaPairs = JSON.parse(challenge.qaPairs);

              const qaImages = [];

              qaPairs.forEach((qa, index) => {
                qaImages.push({
                  type: qa.questionType,
                  content: qa.question,
                  pair: index,
                });
                qaImages.push({
                  type: qa.answerType,
                  content: qa.answer,
                  pair: index,
                });
              });

              setPairImages(qaImages);
            }
          }

          if (challenge.additional_challenges) {
            let array = challenge.additional_challenges
              .split(",")
              .filter((c) => c && c !== "");

            getOtherChallenges(array)
              .then(({ challenges }) => {
                setOtherChallenges(challenges);
                if (challenges.length !== 0) {
                  setAdditionalChallengeType(
                    challenges[0]?.type
                      ? pluralize(challenges[0]?.type)
                      : pluralize("Challenge")
                  );
                }
              })
              .catch((errorMessage) => {
                setOpen(true);
                setResponse({
                  status: false,
                  message: errorMessage,
                });
              });
          }
        })
        .catch((errorMessage) => {
          setOpen(true);
          setResponse({
            status: false,
            message: errorMessage,
          });
        })
        .then(() => {
          setLoading(false);
        });
    } else {
      const decodedChallengeName = challengeName?.replace(/-/g, " ");
      getChallengesByName(decodedChallengeName)
        .then(({ challenge }) => {
          setChallenge(challenge);

          getOtherChallenges(challenge.id, challenge.challenge_pack_id)
            .then(({ challenges }) => {
              setOtherChallenges(challenges);
            })
            .catch((errorMessage) => {
              setOpen(true);
              setResponse({
                status: false,
                message: errorMessage,
              });
            });
        })
        .catch((errorMessage) => {
          setOpen(true);
          setResponse({
            status: false,
            message: errorMessage,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [challengeName, challengeId]);

  useEffect(() => {
    if (challenge && challenge.type === "Poirot" && challenge.gameSection) {
      const gameSection = JSON.parse(challenge.gameSection);
      let newSymtoms = [];

      gameSection.clues.forEach((clue, index) => {
        newSymtoms[index] = {
          clue: clue,
          points: 2 * gameSection.clues.length - 1,
          image: JSON.parse(challenge.images)[index],
        };
      });

      setSymptoms(newSymtoms);
      setConditions(gameSection.correctAnswer);
      setDisplayTime(gameSection.displayTime);
    }
  }, [challenge]);

  const evaluteResult = (result, time) => {
    setGameResult(result);
    setGameOver(true);
    setIsAnswered(true);

    let payload = {
      npi: npi,
      hem: hem,
      ipAddress: ipAddress,
      engagementRefId: "Challenge",
      engagementSecRefId: challenge.id,
      engagementType: "Challenge Completed",
      utmSource: utmSource,
      utmMedium: utmMedium,
      utmCampaign,
      timeToComplete: time,
      gameResult: result ? "Success" : "Failure",
    };
    logEngagement(payload);
  };

  const handleLinkClick = (event, refType, link) => {
    event.preventDefault(); // optional: prevents the default navigation
    console.log("Link clicked:", link);

    let payload = {
      npi: npi,
      hem: hem,
      ipAddress: ipAddress,
      engagementRefId: "Challenge",
      engagementSecRefId: refType,
      distinationUrl: link,
      engagementType: "link_click",
      utmSource: utmSource,
      utmMedium: utmMedium,
      utmCampaign,
      timeToComplete: "",
      gameResult: "",
    };
    logEngagement(payload);

    const url = new URL(link);
    if (npi) url.searchParams.set("npi", npi);
    if (hem) url.searchParams.set("hem", hem);
    if (utmSource) url.searchParams.set("utm_source", utmSource);
    if (utmMedium) url.searchParams.set("utm_medium", utmMedium);
    if (utmCampaign) url.searchParams.set("utm_campaign", utmCampaign);

    const finalLink = url.toString();

    // You can also do custom logic here (e.g. analytics, conditional redirects)
    window.open(finalLink, "_blank"); // open in new tab manually
  };

  if (loading) {
    return <LoadingSpinner message={`Loading Challenge...`} />;
  }

  return (
    <>
      {challenge && challenge.is_active === "1" ? (
        <Container
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: { xs: 0 },
            px: { xs: 0 },
            textAlign: { sm: "center", md: "left" },
            maxWidth: "unset !important",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: { xs: 2, sm: 3 },
              py: { xs: 0 },
              px: { xs: 0 },
              textAlign: { sm: "center", md: "left" },
              width: "100%",
            }}
            id="main"
          >
            <Box
              width={"100%"}
              display={"flex"}
              flexDirection={{ md: "row", sm: "column", xs: "column" }}
              justifyContent={"space-between"}
              alignItems={"center"}
              px={{ sm: 6, xs: 2 }}
              py={2}
              borderBottom={"1px solid #707070"}
            >
              <Box
                component={"img"}
                width={{ sm: "unset", xs: "60%" }}
                src={
                  settings && settings.site_logo !== ""
                    ? process.env.REACT_APP_API_IMAGE_URL + settings.site_logo
                    : "/assets/images/POCN_Logo_TM_285x120.png"
                }
              ></Box>
              {challenge.sponsor_logo_image && (
                <Box
                  display={"flex"}
                  justifyContent={"center"}
                  alignItems={"center"}
                >
                  <Typography>Brought to you by</Typography>
                  <Box
                    component="a"
                    href={challenge.sponsor_logo ? challenge.sponsor_logo : "/"}
                    onClick={(e) =>
                      handleLinkClick(
                        e,
                        "Sponsor Logo",
                        challenge.sponsor_logo ? challenge.sponsor_logo : "/"
                      )
                    }
                    textAlign={"center"}
                    width={{ sm: "unset", xs: "50%" }}
                    target="_blank"
                  >
                    <Box
                      component={"img"}
                      width={{ sm: "100%", xs: "100%" }}
                      src={
                        settings && settings.site_logo !== ""
                          ? process.env.REACT_APP_API_IMAGE_URL +
                            challenge.sponsor_logo_image
                          : "/assets/images/POCN_Logo_TM_285x120.png"
                      }
                    ></Box>
                  </Box>
                </Box>
              )}
            </Box>
            <Box
              width="100%"
              px={{ sm: "48px", xs: "24px" }}
              display="flex"
              flexDirection="column"
              gap={{ sm: 3, xs: 2 }}
              alignItems="center"
            >
              {challenge && (challenge.pi_link || challenge.isi_link) && (
                <Box
                  display={{ sm: "flex", xs: "flex" }}
                  flexDirection={{ md: "row", xs: "column" }}
                  justifyContent={{ sm: "center" }}
                >
                  {challenge.pi_link && (
                    <Link
                      href={challenge.pi_link}
                      onClick={(e) =>
                        handleLinkClick(
                          e,
                          `PI - ${
                            challenge.pl_text
                              ? challenge.pl_text
                              : "Full Prescribing Information"
                          }`,
                          challenge.pi_link
                        )
                      }
                      target="_blank"
                      sx={{
                        color: `${
                          challenge &&
                          challenge.pl_color &&
                          challenge.pl_color !== ""
                            ? challenge.pl_color
                            : "#4933DA"
                        }`,
                        fontWeight: 700,
                        fontFamily: "'Poppins'",
                        pr: { md: 1, xs: 0 },
                        borderRight: {
                          md: `1px solid ${
                            challenge &&
                            challenge.pl_color &&
                            challenge.pl_color !== ""
                              ? challenge.pl_color
                              : "#4933DA"
                          }`,
                          sm: "none",
                        },
                        textDecoration: "underline",
                      }}
                    >
                      {challenge.pl_text
                        ? challenge.pl_text
                        : "Full Prescribing Information"}
                    </Link>
                  )}
                  {challenge.isi_link && (
                    <Link
                      href={challenge.isi_link}
                      onClick={(e) =>
                        handleLinkClick(
                          e,
                          `ISI - ${
                            challenge.isi_text
                              ? challenge.isi_text
                              : "Important Safety Information"
                          }`,
                          challenge.isi_link
                        )
                      }
                      target="_blank"
                      sx={{
                        color: `${
                          challenge &&
                          challenge.pl_color &&
                          challenge.pl_color !== ""
                            ? challenge.pl_color
                            : "#4933DA"
                        }`,
                        fontWeight: 700,
                        fontFamily: "'Poppins'",
                        ml: { md: 1, xs: 0 },
                        textDecoration: "underline",
                      }}
                    >
                      {challenge.isi_text
                        ? challenge.isi_text
                        : "Important Safety Information"}
                    </Link>
                  )}
                </Box>
              )}
              {challenge && challenge.brand_logo && (
                <a
                  href={
                    challenge.brand_logo_link ? challenge.brand_logo_link : "/"
                  }
                  onClick={(e) =>
                    handleLinkClick(
                      e,
                      "Company Logo",
                      challenge.brand_logo_link
                        ? challenge.brand_logo_link
                        : "/"
                    )
                  }
                  target="_blank"
                >
                  <Box
                    component={"img"}
                    src={
                      process.env.REACT_APP_API_IMAGE_URL + challenge.brand_logo
                    }
                    width={{ sm: "unset", xs: "50%" }}
                    sx={{ mt: -1 }}
                  ></Box>
                </a>
              )}
              {challenge && challenge.indication_info && (
                <Box
                  border={`1px solid ${
                    challenge.indication_color
                      ? challenge.indication_color
                      : "#4933DA"
                  }`}
                  py={2}
                  width={"100%"}
                  borderRadius={2}
                >
                  <Typography
                    sx={{
                      color: "#4933DA",
                      textAlign: "left",
                      px: 0,
                    }}
                    className="challenge-indication"
                    dangerouslySetInnerHTML={{
                      __html: decodeHtml(challenge.indication_info),
                    }}
                  />
                </Box>
              )}
              {challenge &&
                challenge.banner_ad_id &&
                challenge.banner_ad_id !== 0 && <AdBanner />}
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              width="100%"
              px={{ md: 6, xs: 3 }}
              pt={{ md: 10 }}
              pb={{ md: 3, xs: 2 }}
              sx={{
                backgroundImage: `url('${
                  challenge && challenge.challenge_bg_image
                    ? process.env.REACT_APP_API_IMAGE_URL +
                      challenge.challenge_bg_image
                    : "/assets/images/default challenge bg.png"
                }')`,
                backgroundSize: { md: "cover", xs: "contain" },
                backgroundRepeat: "no-repeat",
                bgcolor: "#001232",
              }}
            >
              <Container
                sx={{
                  width: "78%",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                  mt: { md: 14, sm: 9, xs: 9 },
                  px: { xs: 0 },
                  maxWidth: "1600px !important",
                }}
              >
                <Typography
                  sx={{
                    fontSize: { md: "42px", sm: "38px", xs: "24px" },
                    fontWeight: 700,
                    color: "#FFFFFF",
                    textAlign: "center",
                  }}
                >
                  {challenge && challenge.description ? (
                    <div
                      className="pack-description"
                      dangerouslySetInnerHTML={{
                        __html: decodeHtml(challenge.description),
                      }}
                    />
                  ) : (
                    `Take the ${challenge ? challenge.name : ""} Challenge!`
                  )}
                </Typography>
                {challenge &&
                  challenge.type === "Quiz" &&
                  challenge.quizzes && (
                    <Quize
                      challenge={challenge}
                      onAnswered={() => setIsAnswered(true)}
                    />
                  )}
                {challenge &&
                  challenge.type === "Pairs" &&
                  (gameOver ? (
                    <GameResult
                      faildMessage={
                        JSON.parse(challenge.gameSection).timeUpMessage
                      }
                      successMessage={
                        JSON.parse(challenge.gameSection).successMessage
                      }
                      isSuccess={gameResult}
                      moreUrl={challenge.more_url}
                      challengeId={challenge.id}
                    />
                  ) : (
                    <Pairs
                      defaultImage={challenge.image}
                      images={pairImages}
                      timeToSolve={
                        JSON.parse(challenge.gameSection).timeToSolve
                      }
                      result={evaluteResult}
                      isStandard={
                        JSON.parse(challenge.gameSection).gameType ===
                        "standard"
                      }
                      isSimpleMode={
                        JSON.parse(challenge.gameSection).versionType ===
                        "simpler"
                      }
                      tileBGColor={challenge.tile_bg_color}
                    />
                  ))}
                {challenge &&
                  challenge.type === "Poirot" &&
                  symptoms.length !== 0 && (
                    <PoirotGame
                      symptoms={symptoms}
                      conditions={conditions}
                      displayTime={displayTime}
                      faildMessage={
                        JSON.parse(challenge.gameSection).timeUpMessage
                      }
                      successMessage={
                        JSON.parse(challenge.gameSection).successMessage
                      }
                      onFinished={() => setIsAnswered(true)}
                      challengeId={challenge.id}
                      moreUrl={challenge.more_url}
                    />
                  )}
              </Container>
            </Box>
          </Box>
          {challenge.foot_note && (
            <Box
              width="100%"
              sx={{ textAlign: "left" }}
              bgcolor="#001232"
              px={{ md: 6, xs: 3 }}
              pb={7}
              display="flex"
              justifyContent="center"
            >
              <Box width="75%" maxWidth="1600px" textAlign="left" px={0}>
                <Typography
                  color="#fff"
                  dangerouslySetInnerHTML={{
                    __html: decodeHtml(challenge.foot_note),
                  }}
                ></Typography>
              </Box>
            </Box>
          )}
          {otherChallenges.filter((oc) => oc.is_active === "1").length !==
            0 && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
              width="100%"
              px="4%"
              pt={{ md: 0, sm: 0, xs: 0 }}
              pb={7}
              bgcolor="#001232"
              mt={-3}
            >
              <Typography
                sx={{
                  fontSize: { md: "30px", sm: "28px", xs: "24px" },
                  fontWeight: 600,
                  color: "#FFFFFF",
                  textAlign: "center",
                  px: 0,
                  pt: 0,
                  pb: 1.5,
                  borderBottom: "2px solid #fff",
                }}
              >
                {/* Additional {additionalChallengeType} */}
                {challenge?.additional_caption
                  ? challenge?.additional_caption
                  : "Additional Challenges"}
              </Typography>
              <Grid
                container
                direction="row"
                justifyContent="center"
                alignItems="center"
                spacing={4}
              >
                {otherChallenges
                  .filter((oc) => oc.is_active === "1")
                  .map((otherChallenge, index) => {
                    return (
                      <Grid item md={4} sm={6} xs={12}>
                        <OtherChallengeItem
                          challengeId={otherChallenge.id}
                          name={otherChallenge.name}
                          link={`${
                            otherChallenge.program_id ? "/program" : ""
                          }${
                            otherChallenge.challenge_pack_id ? "/pack" : ""
                          }/challenge/${
                            otherChallenge.short_url &&
                            challenge.short_url !== ""
                              ? otherChallenge.short_url
                              : otherChallenge.name
                          }?challengeid=${otherChallenge.id}${
                            queryParams.toString()
                              ? `&${queryParams.toString()}`
                              : ""
                          }`}
                        />
                      </Grid>
                    );
                  })}
              </Grid>
            </Box>
          )}
          {challenge &&
          challenge.optional_content &&
          challenge.is_always_show_optional === "1" ? (
            <Box
              width="100%"
              px={{ sm: "48px", xs: "24px" }}
              py="2%"
              sx={{ textAlign: "left" }}
              dangerouslySetInnerHTML={{
                __html: decodeHtml(challenge.optional_content),
              }}
            ></Box>
          ) : (
            isAnswered && (
              <Box
                width="100%"
                px="4%"
                pb={5}
                sx={{ textAlign: "left" }}
                dangerouslySetInnerHTML={{
                  __html: decodeHtml(challenge.optional_content),
                }}
              ></Box>
            )
          )}

          {challenge && challenge.isi && (
            <ISISection
              isiText={challenge.isi}
              isSticky={challenge.stiky_isi === "1"}
            />
          )}
          <Box
            display="flex"
            flexDirection="column"
            alignItems={
              challenge && challenge.company_info_align
                ? challenge.company_info_align === "left"
                  ? "flex-start"
                  : challenge.company_info_align === "center"
                  ? "center"
                  : "flex-end"
                : "center"
            }
            gap={1}
            width="100%"
            px={{ sm: "48px", xs: "24px" }}
            py={5}
          >
            {challenge && challenge.company_logo && (
              <Box
                component="img"
                src={
                  process.env.REACT_APP_API_IMAGE_URL + challenge.company_logo
                }
                width={{ sm: "unset", xs: "50%" }}
                alt="logo of sitemark"
              ></Box>
            )}
            {challenge && challenge.company_info && (
              <Typography
                sx={{
                  textAlign:
                    challenge && challenge.company_info_align
                      ? challenge.company_info_align
                      : "center",
                  width: { md: "50%", sm: "70%", xs: "100%" },
                }}
                dangerouslySetInnerHTML={{
                  __html: decodeHtml(challenge.company_info),
                }}
              ></Typography>
            )}
            {challenge &&
              (challenge.legal_notice_link ||
                challenge.privacy_policy_link) && (
                <Box>
                  {challenge.legal_notice_link && (
                    <Link
                      href={challenge.legal_notice_link}
                      onClick={(e) =>
                        handleLinkClick(
                          e,
                          "Legal Notice",
                          challenge.legal_notice_link
                        )
                      }
                      target="_blank"
                      sx={{
                        color: challenge.ln_pp_color
                          ? challenge.ln_pp_color
                          : "#DA00B5",
                        pr: 2,
                        borderRight: `${
                          challenge.privacy_policy_link
                            ? "1px solid #4933DA"
                            : "none"
                        }`,
                      }}
                    >
                      Legal Notice
                    </Link>
                  )}
                  {challenge.privacy_policy_link && (
                    <Link
                      href={challenge.privacy_policy_link}
                      onClick={(e) =>
                        handleLinkClick(
                          e,
                          "Privacy Policy",
                          challenge.privacy_policy_link
                        )
                      }
                      target="_blank"
                      sx={{
                        color: challenge.ln_pp_color
                          ? challenge.ln_pp_color
                          : "#DA00B5",
                        ml: 2,
                      }}
                    >
                      Privacy Policy
                    </Link>
                  )}
                </Box>
              )}
          </Box>
          <Container
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: { xs: 4, sm: 8 },
              py: { xs: 3, sm: 4 },
              px: { sm: "48px", xs: "24px" },
              textAlign: { sm: "center", md: "left" },
              bgcolor: "#fff",
              borderTop: "1px solid #707070",
              maxWidth: "unset !important",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { md: "row", sm: "column", xs: "column" },
                gap: { md: 5, sm: 4, xs: 4 },
                width: "100%",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: { md: "flex-start", sm: "center", xs: "center" },
                  gap: 4,
                  width: { md: "45%", sm: "100%" },
                }}
              >
                <a href="http://pocn.com/" target="_blank">
                  <Box
                    component="img"
                    src={`${
                      settings && settings.footer_logo
                        ? process.env.REACT_APP_API_IMAGE_URL +
                          settings.footer_logo
                        : "/assets/images/POCN_Logo-White_TM_285x120.png"
                    }`}
                    sx={{ width: { sm: "200px", xs: "60%" } }}
                    alt="logo"
                  ></Box>
                </a>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { md: "row", sm: "column", xs: "column" },
                  gap: { md: 5, sm: 3, xs: 1 },
                  width: { md: "50%", sm: "100%", xs: "100%" },
                  justifyContent: "space-between",
                }}
              >
                <Box display="flex" flexDirection="column" gap={1}>
                  <Link
                    href={
                      settings && settings.privacy_policy_link
                        ? settings.privacy_policy_link
                        : "#"
                    }
                    onClick={(e) =>
                      handleLinkClick(
                        e,
                        "Privacy Policy",
                        settings && settings.privacy_policy_link
                          ? settings.privacy_policy_link
                          : "#"
                      )
                    }
                    target="_blank"
                    sx={{ color: "#001232" }}
                  >
                    Privacy Policy
                  </Link>
                  <Link
                    href={
                      settings && settings.americas_top_link
                        ? settings.americas_top_link
                        : "#"
                    }
                    onClick={(e) =>
                      handleLinkClick(
                        e,
                        "Your Privacy Choices",
                        settings && settings.americas_top_link
                          ? settings.americas_top_link
                          : "#"
                      )
                    }
                    target="_blank"
                    sx={{ color: "#001232" }}
                  >
                    Your Privacy Choices
                    <img src={privacyOptions} alt="privacy options" class="img-privacy" />
                  </Link>
                </Box>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Link
                    href={
                      settings && settings.contact_us_link
                        ? settings.contact_us_link
                        : "#"
                    }
                    onClick={(e) =>
                      handleLinkClick(
                        e,
                        "Contact Us",
                        settings && settings.contact_us_link
                          ? settings.contact_us_link
                          : "#"
                      )
                    }
                    target="_blank"
                    sx={{ color: "#001232" }}
                  >
                    Contact Us
                  </Link>
                  <Link
                    href={
                      settings && settings.terms_link
                        ? settings.terms_link
                        : "#"
                    }
                    onClick={(e) =>
                      handleLinkClick(
                        e,
                        "Terms of Use",
                        settings && settings.terms_link
                          ? settings.terms_link
                          : "#"
                      )
                    }
                    target="_blank"
                    sx={{ color: "#001232" }}
                  >
                    Terms of Use
                  </Link>
                </Box>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Link
                    href={
                      settings && settings.pocn_plus_link
                        ? settings.pocn_plus_link
                        : "#"
                    }
                    onClick={(e) =>
                      handleLinkClick(
                        e,
                        "POCN+",
                        settings && settings.pocn_plus_link
                          ? settings.pocn_plus_link
                          : "#"
                      )
                    }
                    target="_blank"
                    sx={{ color: "#001232" }}
                  >
                    POCN+
                  </Link>
                  <Link
                    href={
                      settings && settings.learning_hub_link
                        ? settings.learning_hub_link
                        : "#"
                    }
                    onClick={(e) =>
                      handleLinkClick(
                        e,
                        "Learning Hub",
                        settings && settings.learning_hub_link
                          ? settings.learning_hub_link
                          : "#"
                      )
                    }
                    target="_blank"
                    sx={{ color: "#001232" }}
                  >
                    Learning Hub
                  </Link>
                </Box>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Link
                    href={`${
                      settings && settings.be_a_leader_link
                        ? settings.be_a_leader_link
                        : "https://www.pocn.com/ambassador-program/"
                    }`}
                    onClick={(e) =>
                      handleLinkClick(
                        e,
                        "Ambassador Program",
                        settings && settings.be_a_leader_link
                          ? settings.be_a_leader_link
                          : "https://www.pocn.com/ambassador-program/"
                      )
                    }
                    target="_blank"
                    sx={{ color: "#001232" }}
                  >
                    Ambassador Program
                  </Link>
                </Box>
              </Box>
            </Box>
            <Box
              width="100%"
              py={3}
              display="flex"
              flexDirection={{ sm: "row", xs: "column" }}
              justifyContent="space-between"
              alignItems="center"
              borderTop="1px solid #707070"
            >
              <Typography
                sx={{
                  fontSize: { md: "17px", sm: "16px", xs: "14px" },
                  fontWeight: 400,
                  color: "#414042",
                  textAlign: { sm: "left", xs: "center" },
                }}
              >
                {copyrightText}
              </Typography>
              <Stack
                direction="row"
                justifyContent="left"
                spacing={1}
                useFlexGap
                sx={{
                  color: "text.secondary",
                }}
              >
                {settings && settings.facebook_link && (
                  <IconButton
                    color="inherit"
                    href={`${
                      settings && settings.facebook_link
                        ? settings.facebook_link
                        : "https://facebook.com/POCNInc"
                    }`}
                    onClick={(e) =>
                      handleLinkClick(
                        e,
                        "facebook footer icon",
                        settings && settings.facebook_link
                          ? settings.facebook_link
                          : "https://facebook.com/POCNInc"
                      )
                    }
                    target="_blank"
                    aria-label="facebook"
                    sx={{ alignSelf: "center" }}
                  >
                    <FacebookRoundedIcon fontSize="medium" />
                  </IconButton>
                )}

                {settings && settings.twitter_link && (
                  <IconButton
                    color="inherit"
                    href={`${
                      settings && settings.twitter_link
                        ? settings.twitter_link
                        : "https://twitter.com/POCNInc"
                    }`}
                    onClick={(e) =>
                      handleLinkClick(
                        e,
                        "twitter footer icon",
                        settings && settings.twitter_link
                          ? settings.twitter_link
                          : "https://twitter.com/POCNInc"
                      )
                    }
                    target="_blank"
                    aria-label="twitter"
                    sx={{ alignSelf: "center" }}
                  >
                    <TwitterIcon fontSize="medium" />
                  </IconButton>
                )}
                {settings && settings.linkedin_link && (
                  <IconButton
                    color="inherit"
                    href={`${
                      settings && settings.linkedin_link
                        ? settings.linkedin_link
                        : "https://www.linkedin.com/company/pocn/"
                    }`}
                    onClick={(e) =>
                      handleLinkClick(
                        e,
                        "linkedin footer icon",
                        settings && settings.linkedin_link
                          ? settings.linkedin_link
                          : "https://www.linkedin.com/company/pocn/"
                      )
                    }
                    target="_blank"
                    aria-label="linkedin"
                    sx={{ alignSelf: "center" }}
                  >
                    <LinkedInIcon fontSize="medium" />
                  </IconButton>
                )}
                {settings && settings.instagram_link && (
                  <IconButton
                    color="inherit"
                    href={`${
                      settings && settings.instagram_link
                        ? settings.instagram_link
                        : "https://www.instagram.com/pocninc/"
                    }`}
                    onClick={(e) =>
                      handleLinkClick(
                        e,
                        "instagram footer icon",
                        settings && settings.instagram_link
                          ? settings.instagram_link
                          : "https://www.instagram.com/pocninc/"
                      )
                    }
                    target="_blank"
                    aria-label="instagram"
                    sx={{ alignSelf: "center" }}
                  >
                    <InstagramIcon fontSize="medium" />
                  </IconButton>
                )}
              </Stack>
            </Box>
          </Container>
        </Container>
      ) : (
        <Container
          component="main"
          maxWidth="xs"
          sx={{
            mt: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Box sx={{ my: 5 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Inactive Page
            </Typography>
            <Typography variant="subtitle1">
              This page is currently under construction or not available for
              public view.
            </Typography>
          </Box>
        </Container>
      )}
    </>
  );
};

export default Challenge;
